import {useCallback, useEffect, useId, useRef, useState, useSyncExternalStore, type RefObject} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {Image} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {gsap} from 'gsap'

import type {GenericAsset} from '../../../../../../brand/lib/types/contentful'
import {hasWebGLSupport} from '../../../../../../lib/utils/platform'

import {WebGLFooter} from '../WebGL/WebGLFooter/WebGLFooter'

import styles from './FooterVisual.module.css'

type Props = {
  fallbackImage?: GenericAsset
}

const subscribeToWebGLSupport = () => () => {}
const getServerWebGLSupport = () => true
const PINK_GRID_RECT_SELECTOR = 'rect[fill="#C05200"]'
const BLOCK_GRID_RECT_SELECTOR = 'rect[fill="#FFE2F3"]'
const BLOCK_GRID_ACCENT_RECT_SELECTOR = 'rect[fill="#583AF7"]'
const TERMINAL_PATH_SELECTOR = 'path[fill="#08872B"]'
const LANGUAGE_PATTERN_POSITIONS = [
  [0, 0],
  [78.8936, 0],
  [157.7844, -44.588],
  [236.6744, -44.588],
  [315.5724, -89.178],
  [394.4624, -89.178],
  [473.3524, -133.7771],
  [552.2544, -133.7771],
  [0, 44.599],
  [78.8936, 44.6],
  [157.7844, 0],
  [236.6744, 0],
  [315.5724, -44.588],
  [394.4624, -44.588],
  [473.3524, -89.178],
  [552.2544, -89.178],
  [69.9221, 210.481],
  [148.8194, 210.481],
  [227.7104, 210.481],
  [306.6004, 165.891],
  [385.5024, 165.891],
  [464.3994, 121.301],
  [543.2824, 121.301],
  [306.6004, 210.481],
  [385.5024, 210.481],
  [464.3994, 165.891],
  [543.2824, 165.891],
] as const

type FooterVisualAnimationRefs = {
  pinkGridRef: RefObject<SVGSVGElement | null>
  languagePatternRef: RefObject<SVGSVGElement | null>
  blockGridRef: RefObject<SVGSVGElement | null>
  terminalRef: RefObject<SVGSVGElement | null>
}

function useFooterVisualAnimation({
  pinkGridRef,
  languagePatternRef,
  blockGridRef,
  terminalRef,
  prefersReducedMotion,
}: FooterVisualAnimationRefs & {prefersReducedMotion: boolean}) {
  useEffect(() => {
    const pinkGrid = pinkGridRef.current
    const languagePattern = languagePatternRef.current
    const blockGrid = blockGridRef.current
    const terminal = terminalRef.current
    if (!pinkGrid || !languagePattern || !blockGrid || !terminal || prefersReducedMotion) return

    const scaleAnimations = [
      {
        element: pinkGrid,
        selector: PINK_GRID_RECT_SELECTOR,
        duration: 0.7,
        stagger: 0.1,
        position: 0,
      },
      {
        element: languagePattern,
        selector: 'use',
        duration: 0.5,
        stagger: 0.03,
        position: 0.2,
      },
      {
        element: blockGrid,
        selector: BLOCK_GRID_RECT_SELECTOR,
        duration: 0.5,
        stagger: 0.05,
        position: 0.4,
      },
      {
        element: blockGrid,
        selector: BLOCK_GRID_ACCENT_RECT_SELECTOR,
        duration: 0.5,
        stagger: 0.05,
        position: 0.7,
      },
    ]
    let observer: IntersectionObserver | undefined
    const context = gsap.context(() => {
      const timeline = gsap.timeline({paused: true})

      for (const {element, selector, duration, stagger, position} of scaleAnimations) {
        timeline.from(
          element.querySelectorAll(selector),
          {
            scale: 0,
            transformOrigin: 'center',
            duration,
            stagger,
            ease: 'expo.out',
          },
          position,
        )
      }

      timeline.from(
        terminal.querySelectorAll(TERMINAL_PATH_SELECTOR),
        {
          x: -304,
          duration: 0.8,
          ease: 'expo.out',
        },
        0.8,
      )

      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return

          timeline.play()
          observer?.disconnect()
        },
        {threshold: 0.5},
      )
      observer.observe(pinkGrid)
    }, pinkGrid)

    return () => {
      observer?.disconnect()
      context.revert()
    }
  }, [blockGridRef, languagePatternRef, pinkGridRef, prefersReducedMotion, terminalRef])
}

export const FooterVisual = ({fallbackImage}: Props) => {
  const languageShapeId = useId()
  const pinkGridRef = useRef<SVGSVGElement>(null)
  const languagePatternRef = useRef<SVGSVGElement>(null)
  const blockGridRef = useRef<SVGSVGElement>(null)
  const terminalRef = useRef<SVGSVGElement>(null)
  const closingBraceRef = useRef<SVGSVGElement>(null)
  const [hasWebGLFailed, setHasWebGLFailed] = useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()
  const hasWebGL = useSyncExternalStore(subscribeToWebGLSupport, hasWebGLSupport, getServerWebGLSupport)
  const handleWebGLFallback = useCallback(() => setHasWebGLFailed(true), [])

  useFooterVisualAnimation({
    pinkGridRef,
    languagePatternRef,
    blockGridRef,
    terminalRef,
    prefersReducedMotion,
  })

  const fallbackImageFields = fallbackImage?.fields
  const fallbackImageDetails = fallbackImageFields?.asset.fields.file.details?.image
  const fallbackImageDescription = fallbackImageFields?.asset.fields.description || ''

  if ((!hasWebGL || hasWebGLFailed || prefersReducedMotion) && fallbackImageFields && fallbackImageDetails) {
    return (
      <Image
        className={styles.FallbackImage}
        src={fallbackImageFields.asset.fields.file.url}
        width={fallbackImageFields.width ?? fallbackImageDetails.width}
        height={fallbackImageFields.height ?? fallbackImageDetails.height}
        alt={fallbackImageDescription}
        loading="lazy"
      />
    )
  }

  return (
    <div
      className={styles.FooterVisual}
      {...(fallbackImageDescription && {role: 'img', 'aria-label': fallbackImageDescription})}
    >
      <svg
        ref={pinkGridRef}
        className={styles.FullHeight}
        aria-hidden="true"
        width="268"
        height="421"
        viewBox="0 0 268 421"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="268" height="422" fill="#FF7DED" />
        <rect x="38.973" y="0.00717735" width="27.6105" height="26.73842265" fill="#C05200" />
        <rect x="149.42" y="0.00742149" width="27.61" height="26.73837851" fill="#C05200" />
        <rect x="-16.2443" y="53.4813" width="27.6105" height="26.7384" fill="#C05200" />
        <rect x="94.1942" y="53.4813" width="27.6108" height="26.7384" fill="#C05200" />
        <rect x="204.641" y="53.4813" width="27.61" height="26.7384" fill="#C05200" />
        <rect x="38.973" y="106.953" width="27.6105" height="26.739" fill="#C05200" />
        <rect x="149.424" y="106.953" width="27.61" height="26.739" fill="#C05200" />
        <rect x="52.8588" y="181.911" width="27.6104" height="26.739" fill="#C05200" />
        <rect x="-2.36633" y="235.394" width="27.61043" height="26.738" fill="#C05200" />
        <rect x="108.076" y="235.394" width="27.611" height="26.738" fill="#C05200" />
        <rect x="52.8588" y="288.877" width="27.6104" height="26.738" fill="#C05200" />
        <rect x="-2.36633" y="342.35" width="27.61043" height="26.739" fill="#C05200" />
        <rect x="108.076" y="342.35" width="27.611" height="26.739" fill="#C05200" />
        <rect x="52.8588" y="395.825" width="27.6104" height="26.738" fill="#C05200" />
      </svg>
      <svg
        ref={languagePatternRef}
        className={styles.FullHeight}
        aria-hidden="true"
        width="532"
        height="421"
        viewBox="0 0 532 421"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_131_4991)">
          <rect y="-16" width="534" height="451" fill="#583AF7" />
          {LANGUAGE_PATTERN_POSITIONS.map(([x, y]) => (
            <use key={`${x}-${y}`} href={`#${languageShapeId}`} x={x} y={y} />
          ))}
        </g>
        <defs>
          <path
            id={languageShapeId}
            d="M-44.8404 201.559C-45.9182 201.559 -46.8109 200.656 -46.8109 199.545C-46.8109 198.446 -45.9182 197.542 -44.8404 197.542C-43.7626 197.542 -42.8699 198.446 -42.8699 199.545C-42.8699 200.656 -43.7626 201.559 -44.8404 201.559ZM-29.501 186.786C-30.3719 186.786 -31.036 186.133 -31.036 185.262C-31.036 184.402 -30.3719 183.749 -29.501 183.749C-28.6301 183.749 -27.966 184.402 -27.966 185.262C-27.966 186.133 -28.6301 186.786 -29.501 186.786ZM-28.4341 189.682V201.429C-28.4341 204.042 -29.7732 205.076 -33.061 205.076C-33.8775 205.076 -35.1403 205.054 -35.9242 204.989V203.334C-35.3581 203.388 -33.8339 203.421 -33.1154 203.421C-30.9163 203.421 -30.2522 202.899 -30.2522 201.211V191.217H-35.652V189.682H-28.4341ZM-18.4728 201.701C-20.7263 201.701 -22.2287 200.286 -22.2287 198.141C-22.2287 196.051 -20.7263 194.81 -18.0373 194.668L-14.7931 194.505V193.438C-14.7931 191.957 -15.6967 191.141 -17.3405 191.141C-18.7014 191.141 -19.6594 191.718 -19.9642 192.73H-21.7823C-21.5646 190.836 -19.7356 189.541 -17.3188 189.541C-14.5427 189.541 -12.9532 190.956 -12.9532 193.416V201.505H-14.7168V199.73H-14.9019C-15.468 200.993 -16.7527 201.701 -18.4728 201.701ZM-17.9393 200.079C-16.1539 200.079 -14.7931 198.892 -14.7931 197.314V195.92L-17.8304 196.062C-19.4852 196.138 -20.3562 196.846 -20.3562 198.076C-20.3562 199.317 -19.4308 200.079 -17.9393 200.079ZM-8.82713 189.682H-6.84575L-3.54707 199.513H-3.40554L-0.128641 189.682H1.84185L-2.46929 201.494H-4.5051L-8.82713 189.682ZM9.09241 201.701C6.83886 201.701 5.33649 200.286 5.33649 198.141C5.33649 196.051 6.83886 194.81 9.52788 194.668L12.7721 194.505V193.438C12.7721 191.957 11.8685 191.141 10.2246 191.141C8.86379 191.141 7.90576 191.718 7.60093 192.73H5.78285C6.00058 190.836 7.82955 189.541 10.2464 189.541C13.0225 189.541 14.612 190.956 14.612 193.416V201.505H12.8483V199.73H12.6633C12.0971 200.993 10.8125 201.701 9.09241 201.701ZM9.62586 200.079C11.4113 200.079 12.7721 198.892 12.7721 197.314V195.92L9.73473 196.062C8.07994 196.138 7.20901 196.846 7.20901 198.076C7.20901 199.317 8.13438 200.079 9.62586 200.079Z"
            fill="white"
          />
          <clipPath id="clip0_131_4991">
            <rect width="532" height="421" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <div className={styles.StackedVisuals}>
        <svg
          ref={blockGridRef}
          className={styles.HalfHeight}
          aria-hidden="true"
          width="304"
          height="209"
          viewBox="0 0 304 209"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_131_5671)">
            <rect width="278" height="209" fill="#BBA00A" />
            <rect x="238" width="62" height="176" fill="#BBA00A" />
            <rect x="-0.118896" y="83.5576" width="42.8545" height="42.8545" fill="#FFE2F3" />
            <rect x="42.7288" y="40.7825" width="42.8545" height="42.8545" fill="#FFE2F3" />
            <rect x="42.7288" y="126.405" width="42.8545" height="42.8545" fill="#FFE2F3" />
            <rect x="85.5881" y="-2.07373" width="42.8545" height="42.8545" fill="#FFE2F3" />
            <rect x="85.5881" y="169.254" width="42.8545" height="42.9459" fill="#FFE2F3" />
            <rect x="128.443" y="40.7825" width="42.8545" height="42.8545" fill="#FFE2F3" />
            <rect x="128.443" y="126.405" width="42.8545" height="42.8545" fill="#FFE2F3" />
            <rect x="171" y="83" width="45.2877" height="42.9041" fill="#FFE2F3" />
            <rect x="42.7288" y="83.5576" width="42.8545" height="42.8545" fill="#583AF7" />
            <rect x="85.5881" y="40.7822" width="42.8545" height="42.8545" fill="#583AF7" />
            <rect x="85.5881" y="83.5576" width="42.8545" height="42.8545" fill="#583AF7" />
            <rect x="85.5881" y="126.405" width="42.8545" height="42.8545" fill="#583AF7" />
            <rect x="128.443" y="83.5576" width="42.8545" height="42.8545" fill="#583AF7" />
          </g>
          <defs>
            <clipPath id="clip0_131_5671">
              <rect width="304" height="209" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <svg
          ref={terminalRef}
          className={styles.HalfHeight}
          aria-hidden="true"
          width="304"
          height="212"
          viewBox="0 0 304 212"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3.05176e-05" width="274" height="212" fill="#5FED83" />
          <rect x="245" y="23" width="44" height="189" fill="#5FED83" />
          <path
            d="M94.9474 107.196L44.3297 157.814C41.2235 160.92 41.2235 165.956 44.3297 169.062C47.4358 172.168 52.4719 172.168 55.578 169.062L111.82 112.82C114.926 109.714 114.926 104.678 111.82 101.572L55.578 45.3296C52.4719 42.2235 47.4358 42.2235 44.3297 45.3296C41.2235 48.4358 41.2235 53.4719 44.3296 56.578L94.9474 107.196Z"
            fill="#08872B"
          />
          <path
            d="M120.354 143.889C115.961 143.889 112.4 147.45 112.4 151.843C112.4 156.236 115.961 159.797 120.354 159.797H178.682C183.075 159.797 186.636 156.236 186.636 151.843C186.636 147.45 183.075 143.889 178.682 143.889H120.354Z"
            fill="#08872B"
          />
        </svg>
      </div>
      <svg
        ref={closingBraceRef}
        className={clsx(styles.FullHeight, styles.FinalItem)}
        aria-hidden="true"
        width="160"
        height="421"
        viewBox="0 0 160 421"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M86.8307 424.274C70.8317 424.274 58.6144 419.707 50.1785 410.573C41.7427 401.44 37.5248 389.507 37.5248 374.775V276.22C37.5248 265.613 35.0523 257.363 30.1071 251.471C25.162 245.283 18.617 242.19 10.472 242.19H0V188.272H10.472C18.9079 188.272 25.4529 185.178 30.1071 178.991C35.0523 172.803 37.5248 164.259 37.5248 153.357V49.4986C37.5248 34.7669 41.7427 22.8342 50.1785 13.7005C58.6144 4.56684 70.8317 0 86.8307 0H159.699V55.244H104.72V156.009C104.72 173.098 101.375 186.209 94.6847 195.343C88.2851 204.182 79.2675 209.78 67.6319 212.137V213.905C80.4311 218.324 89.7396 225.543 95.5574 235.56C101.666 245.283 104.72 257.511 104.72 272.242V369.472H159.699V424.274H86.8307Z"
          fill="#168FFB"
        />
      </svg>
      <WebGLFooter onFallback={handleWebGLFallback} />
    </div>
  )
}
